/**
 * The ONE typed surface every page talks to. `client.ts` picks the implementation:
 * `real.ts` (Supabase RPCs + edge functions, names per SPEC §2–§3) or `mock/` (in-memory fake,
 * VITE_MOCK=1). Pages never import supabase-js directly.
 */
import type * as T from './types';

export class ApiError extends Error {
  code: string;
  fields: T.FieldError[];
  constructor(message: string, code = 'error', fields: T.FieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.fields = fields;
  }
}

export interface Session { user_id: string; email: string }
export type Unsubscribe = () => void;

export interface Api {
  readonly mode: 'mock' | 'real';

  // ── Auth ──
  getSession(): Promise<Session | null>;
  onAuthChange(cb: (s: Session | null) => void): Unsubscribe;
  signInWithPassword(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, displayName: string): Promise<{ needsConfirmation: boolean }>;
  sendMagicLink(email: string): Promise<void>;
  signOut(): Promise<void>;
  me(): Promise<T.Me>;

  // ── Accounts ──
  myAccounts(): Promise<T.MyAccount[]>;
  myIndividualAccount(): Promise<T.Account>;
  createDealerAccount(input: { company_name: string; website: string; display_name: string; slug: string; company_id?: string | null }): Promise<string>;
  updateStorefront(accountId: string, storefront: T.Storefront, displayName: string): Promise<void>;
  members(accountId: string): Promise<T.Member[]>;
  inviteMember(accountId: string, email: string, role: T.MemberRole): Promise<string>;
  setMemberRole(accountId: string, userId: string, role: T.MemberRole): Promise<void>;
  removeMember(accountId: string, userId: string): Promise<void>;
  /** Pending invites have no user_id yet — they are addressed by email. */
  setInviteRole(accountId: string, email: string, role: T.MemberRole): Promise<void>;
  cancelInvite(accountId: string, email: string): Promise<void>;
  setAutoRecharge(accountId: string, enabled: boolean, thresholdCents: number, amountCents: number): Promise<void>;
  getSettings(accountId: string): Promise<T.AccountSettings>;
  saveSettings(accountId: string, settings: T.AccountSettings): Promise<void>;

  // ── Taxonomy ──
  attributeDefs(kind: T.ListingKind): Promise<T.AttributeDef[]>;
  makes(aircraftClass?: string | null): Promise<T.Make[]>;
  models(makeId: number): Promise<T.Model[]>;

  // ── Listings ──
  quote(accountId: string, kind: T.ListingKind): Promise<T.Quote>;
  myListings(accountId: string): Promise<T.MyListing[]>;
  saveListing(input: T.ListingInput): Promise<string>;
  submitListing(id: string): Promise<T.SubmitResult>;
  setListingState(id: string, state: T.ListingState): Promise<void>;
  comps(listingId: string): Promise<T.Comps>;
  /** Converts/compresses to JPEG client-side first (WebP/HEIC would skip stolen-photo hashing). */
  uploadListingPhoto(file: File): Promise<{ path: string; url: string }>;
  uploadListingDocument(listingId: string, file: File, kind: T.ListingDocument['kind'], isPublic: boolean): Promise<T.ListingDocument>;
  deleteListingDocument(docId: string): Promise<void>;
  schedulePriceChange(listingId: string, priceCents: number, effectiveAt: string): Promise<T.ScheduledPriceChange>;
  cancelPriceChange(id: string): Promise<void>;

  // ── Import / feeds ──
  importPreview(accountId: string, csv: string): Promise<T.ImportPreview>;
  importRun(accountId: string, csv: string, mapping: Record<string, string | null>): Promise<T.ImportRun>;
  importRuns(accountId: string): Promise<T.ImportRun[]>;
  feeds(accountId: string): Promise<T.Feed[]>;
  saveFeed(feed: Partial<T.Feed> & { account_id: string; name: string; url: string; format: T.Feed['format'] }): Promise<string>;
  deleteFeed(id: string): Promise<void>;
  runFeedNow(id: string): Promise<T.ImportRun>;

  // ── Leads ──
  inbox(accountId: string, filter: T.InboxFilter): Promise<T.InboxRow[]>;
  leadThread(leadId: string): Promise<T.LeadThread>;
  sendMessage(leadId: string, body: string, attachments?: T.Attachment[]): Promise<string>;
  markRead(leadId: string): Promise<void>;
  updateLead(leadId: string, patch: T.LeadPatch): Promise<void>;
  uploadLeadAttachment(leadId: string, file: File): Promise<T.Attachment>;
  savedReplies(accountId: string): Promise<T.SavedReply[]>;
  saveSavedReply(reply: { id?: string; account_id: string; title: string; body: string }): Promise<string>;
  deleteSavedReply(id: string): Promise<void>;
  /** Realtime: fires on any lead / message change for the account. */
  subscribeLeads(accountId: string, onChange: (e: { lead_id: string; kind: 'lead' | 'message' }) => void): Unsubscribe;

  // ── Campaigns ──
  campaigns(accountId: string): Promise<T.Campaign[]>;
  campaign(id: string): Promise<T.Campaign>;
  saveCampaign(input: T.CampaignInput): Promise<string>;
  saveCreative(input: T.CreativeInput): Promise<string>;
  setCampaignStatus(id: string, status: 'active' | 'paused' | 'ended'): Promise<T.StatusChangeResult>;
  estimateAudience(targeting: T.Targeting): Promise<T.AudienceEstimate>;
  campaignStats(accountId: string, from: string, to: string): Promise<T.CampaignStats>;
  dailyActivity(accountId: string, from: string, to: string): Promise<T.DailyActivity[]>;
  currentPolicy(): Promise<T.PricingPolicy>;
  /** Public bucket mkt-creatives, path <account_id>/creatives/… (owner/admin may write). */
  uploadCreativeImage(accountId: string, file: File): Promise<{ path: string; url: string }>;
  /** hh_mkt_delete_creative — a live campaign cannot lose its last creative. */
  deleteCreative(id: string): Promise<void>;

  // ── Billing ──
  billingSummary(accountId: string): Promise<T.BillingSummary>;
  ledger(accountId: string, from?: string, to?: string): Promise<T.LedgerRow[]>;
  redeemCode(accountId: string, code: string): Promise<T.RedeemResult>;
  /** mkt-stripe setup_card → SetupIntent client secret ('mock_…' in mock mode). */
  setupCard(accountId: string): Promise<{ client_secret: string }>;
  /** Mock only: pretend Stripe confirmed the SetupIntent. Real mode: no-op (webhook does it). */
  confirmMockCard(accountId: string, card: { brand: string; last4: string }): Promise<void>;
  listCards(accountId: string): Promise<T.Card[]>;
  setDefaultCard(accountId: string, paymentMethodId: string): Promise<void>;
  topup(accountId: string, amountCents: number): Promise<{ client_secret: string }>;
  confirmMockTopup(accountId: string, amountCents: number): Promise<void>;
  identityStart(accountId: string): Promise<{ url: string }>;
  portalLink(accountId: string): Promise<{ url: string }>;

  // ── Settings: API keys & webhooks ──
  apiKeys(accountId: string): Promise<T.ApiKey[]>;
  createApiKey(accountId: string, name: string): Promise<T.NewApiKey>;
  revokeApiKey(id: string): Promise<void>;
  webhooks(accountId: string): Promise<T.Webhook[]>;
  /** hh_mkt_create_webhook → signing secret shown ONCE. There is no update RPC: change = delete + create. */
  createWebhook(accountId: string, url: string, events: T.WebhookEvent[]): Promise<T.NewWebhook>;
  deleteWebhook(id: string): Promise<void>;

  // ── Staff ──
  isStaff(): Promise<boolean>;
  adminOverview(from: string, to: string): Promise<T.AdminOverview>;
  adminPolicies(): Promise<T.PricingPolicy[]>;
  adminSetPolicy(policy: Omit<T.PricingPolicy, 'id' | 'created_by'>): Promise<string>;
  adminPrograms(): Promise<T.CreditProgram[]>;
  adminCreateProgram(p: Omit<T.CreditProgram, 'id' | 'created_by' | 'created_at' | 'redemptions'>): Promise<string>;
  adminGrants(): Promise<T.CreditGrant[]>;
  adminGrantCredit(input: { account_id: string; amount_cents: number; expires_days: number; applies_to: T.SpendPurpose[]; note: string; program_id?: string | null }): Promise<string>;
  adminAccounts(q?: string): Promise<T.AdminAccountRow[]>;
  adminSetAccount(accountId: string, patch: T.AdminAccountPatch): Promise<void>;
  adminReviewRuns(filter?: { decision?: string; target_type?: string }): Promise<T.ReviewRun[]>;
  adminOverrideReview(targetType: 'listing' | 'creative' | 'account', id: string, decision: T.ReviewStatus, reason: string): Promise<void>;
  adminAuditLog(filter?: { action?: string }): Promise<T.AuditEntry[]>;
}
